import React, { useContext, useMemo, useState } from "react";

import {
  Button,
  Card,
  CardActionArea,
  Chip,
  Dialog,
  GlobalStyles,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { IosShare, RoomOutlined } from "@mui/icons-material";

import { ReactComponent as LogoBranco } from "libAssets/logoBranco.svg";
import { RETRO_PAGES } from "components/retrospective/RetrospectiveData";
import { useUserContext } from "contexts/UserContext";
import { useLocation } from "react-router-dom";
import { RetroContext } from "components/retrospective/RetrospectiveMain";

const useStyles = makeStyles()((theme) => ({
  outer: {
    flex: 1,
    display: "grid",
    height: "100%",
    width: "100%",
    gap: theme.spacing(3),

    // vertical
    gridTemplateRows: "repeat(5, 1fr)",
    gridTemplateColumns: "repeat(1, 1fr)",

    // horizontal
    "@media (orientation: landscape)": {
      gridTemplateRows: "repeat(5, 1fr)",
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },
  logo: {
    // vertical
    gridRow: "span 1",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",

    "& > svg": {
      maxWidth: "25%",
      minWidth: "150px",
    },

    // horizontal
    "@media (orientation: landscape)": {
      gridColumn: "span 3",
      alignItems: "center",
    },
  },
  header: {
    flexDirection: "column",
    justifyContent: "space-around",
    gridRow: "span 1",

    // horizontal
    "@media (orientation: landscape)": {
      gridColumn: "span 3",
    },

    "& > div > div > .MuiChip-icon": {
      color: "white",
    },

    "& > h1": {
      fontSize: "40px",
      lineHeight: "50px",
      background:
        "linear-gradient(90deg, #FFBD26 0%, var(--yellow-dark, #F2BB4F) 50%, #FFEBBE 100%)",
      backgroundClip: "text",
      color: "transparent",
      textAlign: "center",
    },
  },
  text: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-end",

    "& > p": { margin: 0 },

    "& > span": {
      background:
        "linear-gradient(90deg, #FFBD26 0%, var(--yellow-dark, #F2BB4F) 50%, #FFEBBE 100%)",
      backgroundClip: "text",
      color: "transparent",
      textAlign: "center",
    },

    // horizontal
    "@media (orientation: landscape)": {
      gridColumn: "span 3",
    },
  },
  button: {
    flexDirection: "column",
    alignItems: "center",

    "& > button": {
      color: "black",
      background:
        "linear-gradient(0deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.00) 100%), linear-gradient(90deg, #7BF1A8 0%, #00D492 50%, #46ECD5 100%)",
    },

    // horizontal
    "@media (orientation: landscape)": {
      gridColumn: "2 / span 1",
      "& > button": {},
    },
  },
  footer: {
    gridRow: "span 1",
    alignItems: "center",
    justifyContent: "center",

    // horizontal
    "@media (orientation: landscape)": {
      gridColumn: "span 3",
    },
  },
  dialog: {
    width: "60vw",
    maxWidth: "300px",
    height: "50vh",
    maxHeight: "500px",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(3),
    padding: theme.spacing(3),
  },
  card: {
    flex: 1,
    width: "100%",

    "& > button": {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      flex: 1,
      alignItems: "center",
      justifyContent: "space-around",
    },
  },
}));

const SHARE_DATA = {
  title: "Retrospectiva Enermatch",
  text: "Descubra quanto sua escola economizou esse ano",
  url: "https://enermatch.com/retrospectiva2025",
};

const bgGradient =
  "linear-gradient(135deg, #096 0%, #009689 50%, #0092B8 100%)";

const FETCH_CONFIG = {
  headers: {
    "Content-Type": "image/png",
  },
};

export const RetroWelcome = ({ history }) => {
  const { classes } = useStyles();
  const { currentSpace } = useUserContext();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { id } = useContext(RetroContext);
  const location = useLocation();
  const { brand, name } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      brand: searchParams.get("brand") || "",
      name: searchParams.get("name") || "",
    };
  }, [location]);

  const canShare = useMemo(
    () => !!(navigator.canShare && navigator.canShare(SHARE_DATA)),
    []
  );
  const feedLink = useMemo(
    () =>
      `https://retrospective-2025.s3.us-east-1.amazonaws.com/${id}/feed_0.png`,
    [id]
  );
  const storyLink = useMemo(
    () =>
      `https://retrospective-2025.s3.us-east-1.amazonaws.com/${id}/story_0.png`,
    [id]
  );

  const handleShareButton = async () => {
    setShowDialog(true);
  };

  const handleShare = async (fileLink) => {
    try {
      const imgBlob = await fetch(fileLink, FETCH_CONFIG).then((res) =>
        res.blob()
      );
      const data = {
        ...SHARE_DATA,
        files: [
          new File([imgBlob], "retrospectiva", {
            type: imgBlob.type,
          }),
        ],
      };
      await navigator.share(data);
    } catch (err) {
      window.open(fileLink);
    }
  };

  const handleClick = () => {
    history.push(RETRO_PAGES.CO2);
  };

  return (
    <Grid item container className={classes.outer}>
      <GlobalStyles
        styles={{
          body: {
            background: `center / cover no-repeat url(/static/img/retro2025/welcome.jpg)`,
          },
        }}
      />
      <Grid item container className={classes.logo}>
        <LogoBranco />
      </Grid>

      <Grid item container className={classes.header}>
        <Typography variant="h1">Retrospectiva 2025</Typography>
        <Grid item container justifyContent="center">
          <Chip
            icon={<RoomOutlined />}
            variant="outlined"
            sx={{ color: "white", fontWeight: "bold" }}
            label={`${brand || currentSpace?.brand || ""} | ${
              name || currentSpace?.schoolName || ""
            }`}
          />
        </Grid>
      </Grid>

      <Grid item container className={classes.text}>
        <Typography
          variant="h2"
          component="p"
          color="white"
          align="center"
          fontWeight="semibold"
        >
          Veja o quanto a sua escola
        </Typography>
        <Typography
          variant="h1"
          component="span"
          color="white"
          sx={{ fontSize: "30px" }}
        >
          descarbonizou
        </Typography>
      </Grid>

      <Grid item container className={classes.button}>
        <Button onClick={handleClick} variant="contained">
          Ver retrospectiva 2025
        </Button>
      </Grid>

      <Grid item container className={classes.footer}>
        <img height="20px" src="/static/img/retro2025/miniLogo.png" alt="" />

        <Typography color="common.white" variant="caption" fontWeight="bold">
          enermatch.com/retrospectiva2025
        </Typography>

        <IconButton sx={{ color: "white" }} onClick={handleShareButton}>
          <IosShare />
        </IconButton>

        <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
          <Grid
            item
            container
            className={classes.dialog}
            sx={{ background: bgGradient }}
          >
            <Card className={classes.card}>
              <CardActionArea
                onClick={() => {
                  canShare ? handleShare(feedLink) : window.open(feedLink);
                }}
              >
                <Typography variant="body2">Formato Feed</Typography>
                <Grid
                  sx={{
                    height: "100px",
                    width: "100px",
                    borderRadius: "18px",
                    background: bgGradient,
                  }}
                />
              </CardActionArea>
            </Card>
            <Card className={classes.card}>
              <CardActionArea
                onClick={() => {
                  canShare ? handleShare(storyLink) : window.open(storyLink);
                }}
              >
                <Typography variant="body2">Formato Story</Typography>
                <Grid
                  sx={{
                    height: "100px",
                    width: "60px",
                    borderRadius: "18px",
                    background: bgGradient,
                  }}
                />
              </CardActionArea>
            </Card>
          </Grid>
        </Dialog>
      </Grid>
    </Grid>
  );
};
